import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface CursorList<T> {
  rows: T[];
  isFetching: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

/**
 * A cursor-paged list endpoint (CLAUDE.md §9) as one growing list of rows.
 * Every list page goes through this, so none can quietly stop at the API's
 * first 25 rows: spread `hasMore` and `onLoadMore` onto ListPage.
 */
export function useCursorList<T>(
  queryKey: QueryKey,
  /** Spread `page` into the list call: it carries the cursor from the second page on. */
  fetchPage: (page: { cursor?: string }) => Promise<CursorPage<T>>,
  options: { enabled?: boolean } = {},
): CursorList<T> {
  const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam === undefined ? {} : { cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
    enabled: options.enabled ?? true,
  });
  return {
    rows: data?.pages.flatMap((p) => p.items) ?? [],
    isFetching,
    hasMore: hasNextPage,
    loadMore: () => void fetchNextPage(),
  };
}
